/**
 * galleryUploadEngine — the framework-free core of GAL-8's
 * background uploads.
 *
 * WHY THIS EXISTS: every gallery upload used to run inside the page
 * component that started it (NewGalleryDialog / GalleryEditor /
 * GalleryTab). Navigating away unmounted the component and the
 * batch died mid-flight — "leaving that screen stops the upload."
 * This engine owns the queue + transfer lifecycles at MODULE level,
 * outside React, so route changes can't kill it. React subscribes
 * to snapshots (see useGalleryUploads) purely for display.
 *
 * WHY A SEPARATE FILE FROM THE MANAGER: everything environment-
 * specific (Supabase, Mux, react-query, toasts, workers) arrives
 * through `EngineDeps`. That keeps this file importable from a bare
 * Node harness — the batch pipeline is proven by simulation, not
 * hope (see the GAL-8 verification harness).
 *
 * PIPELINE (the speed half of the fix): the old path ran the FULL
 * CPU prep pass (decode + downscale + re-encode of every file),
 * and only then started uploading. For a 40-file batch that's tens
 * of seconds of dead network time. Here prep and upload are two
 * concurrent worker pools connected by a queue — file 1 is on the
 * wire while file 4 is still being compressed. Prep stays bounded
 * (CPU) and upload concurrency is tuned for the small post-prep
 * files (~1–3 MB each).
 *
 * Ordering: sort_order is assigned from the item's INPUT index
 * (startingSortOrder + index), not completion order, so a pipeline
 * race can never shuffle the gallery.
 *
 * FIX-2 added three things to the video half:
 *   • a 'stalled' stage — uploaded to Mux, transcode never confirmed.
 *     The manager's poll used to resolve these as 'done', reporting
 *     success for videos it had never seen finish.
 *   • per-video isolation (uploadOneVideo) so one rejected file can't
 *     hold up the rest of the batch.
 *   • retryVideo(itemId), which resends a failed video using the File
 *     the studio already picked — no second trip to the file picker.
 */

// ── Public state model ───────────────────────────────────────────

export type UploadStage =
  | 'queued'
  | 'preparing'
  | 'uploading'
  | 'saving'
  /** Videos only — bytes are up, Mux is transcoding. Terminal for
   *  the engine; display surfaces keep showing it until the
   *  manager's poll (or the page's own refetch) flips the tile. */
  | 'processing'
  /**
   * FIX-2 — videos only. The bytes reached Mux but we stopped waiting
   * without a verdict (poll deadline reached, or the status service
   * was unreachable). This is NOT 'done' and NOT 'failed': the old
   * code reported these as SUCCESS, which is the one thing a delivery
   * tool must never do. Terminal for settling, but rendered as
   * "unconfirmed" with an explanation, never as a green check.
   */
  | 'stalled'
  | 'done'
  | 'failed';

export interface UploadItemState {
  id: string;
  name: string;
  kind: 'photo' | 'video';
  stage: UploadStage;
  /** 0..1 transfer progress while stage === 'uploading'. */
  progress: number;
  attempt: number;
  message?: string;
}

export interface UploadBatchState {
  id: string;
  galleryId: string;
  galleryName: string;
  /// GAL-14 — null for a gallery that belongs to no project.
  projectId: string | null;
  items: UploadItemState[];
  /** True once every item reached a terminal stage. */
  settled: boolean;
  startedAt: number;
}

export interface EngineSnapshot {
  batches: UploadBatchState[];
  /** Items still moving (not done / failed / processing). */
  inFlightItems: number;
  /** Convenience for the tray pill: totals across UNSETTLED batches. */
  activeTotal: number;
  activeDone: number;
  activeFailed: number;
}

// ── Inputs ───────────────────────────────────────────────────────

/** Minimal file surface so Node tests can pass plain objects. */
export interface FileLike {
  name: string;
  size: number;
  type: string;
}

export interface PhotoInput {
  file: FileLike;
  /** Display name (pre-HEIC-conversion name). */
  name: string;
  /** Full bucket-relative storage path, computed by the caller. */
  path: string;
}

export interface VideoInput {
  file: FileLike;
  name: string;
}

export interface EnqueueInput {
  galleryId: string;
  galleryName: string;
  /// GAL-14 — null for a gallery that belongs to no project.
  projectId: string | null;
  userId: string;
  startingSortOrder: number;
  photos: PhotoInput[];
  videos: VideoInput[];
}

// ── Dependencies (all environment-specific work) ─────────────────

export interface PreparedPhoto {
  file: FileLike;
  width: number | null;
  height: number | null;
}

export interface InsertedPhoto {
  id: string;
  url: string;
}

export interface BatchResult {
  batch: UploadBatchState;
  galleryId: string;
  /// GAL-14 — null for a gallery that belongs to no project.
  projectId: string | null;
  /** Successfully inserted gallery_photos rows (for Nova tagging). */
  insertedPhotos: InsertedPhoto[];
  photosFailed: number;
  videosUploaded: number;
  videosFailed: number;
}

export interface EngineDeps {
  /** HEIC conversion + downscale/recompress + dimension read. Must
   *  never reject for decode issues (imagePrep contract) — a real
   *  rejection fails the item. */
  prepPhoto(file: FileLike): Promise<PreparedPhoto>;
  /** ONE upload attempt. Retries/backoff live in the engine. */
  uploadPhoto(args: {
    file: FileLike;
    path: string;
    onProgress: (fraction: number) => void;
  }): Promise<{ url: string; path: string }>;
  /** Insert the gallery_photos row; resolves with the new row id. */
  insertPhotoRow(row: {
    galleryId: string;
    fileUrl: string;
    name: string;
    sortOrder: number;
    width: number | null;
    height: number | null;
  }): Promise<{ id: string }>;
  /** Find-or-create the gallery's companion delivery (videos). */
  ensureVideoDelivery(
    galleryId: string,
    galleryName: string,
    userId: string,
  ): Promise<{ id: string }>;
  createVideoUpload(
    deliveryId: string,
    fileName: string,
    fileSize: number,
  ): Promise<{ upload_url: string; delivery_file_id: string }>;
  uploadVideo(
    uploadUrl: string,
    file: FileLike,
    onProgress: (fraction: number) => void,
  ): Promise<void>;
  /** Fires exactly once per batch after every item settles —
   *  invalidation + toasts + Nova tagging live here, OUTSIDE any
   *  page component. */
  onBatchSettled(result: BatchResult): void;
  /** Fires after each successful photo row insert (throttled cache
   *  refresh so the grid fills while the batch runs). */
  onPhotoSaved?(galleryId: string): void;
  /** After a video's bytes land (delivery_file_id known) — the
   *  manager hooks its transcode poll here. */
  onVideoUploaded?(galleryId: string, deliveryId: string, deliveryFileId: string, itemId: string): void;
  /** Test hook — timeline of pipeline events. */
  onEvent?(event: string, itemId: string, at: number): void;
  /** Injectable clock + sleep so tests run instantly. */
  now?(): number;
  sleep?(ms: number): Promise<void>;
}

export interface EngineOptions {
  /** CPU-bound decode/encode pool width. */
  prepConcurrency?: number;
  /** Network pool width. Post-prep gallery JPEGs are ~1–3 MB, so a
   *  wider pool than the old 4 amortizes per-request latency
   *  without hammering the edge. */
  uploadConcurrency?: number;
  maxAttempts?: number;
  backoffBaseMs?: number;
}

// ── Internal item bookkeeping ────────────────────────────────────

interface PhotoWork {
  itemId: string;
  batchId: string;
  input: PhotoInput;
  sortOrder: number;
  prepared?: PreparedPhoto;
}

let idCounter = 0;
const nextId = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}-${(idCounter++).toString(36)}`;

export class GalleryUploadEngine {
  private deps: EngineDeps;
  private prepConcurrency: number;
  private uploadConcurrency: number;
  private maxAttempts: number;
  private backoffBaseMs: number;

  private batches = new Map<string, UploadBatchState>();
  private items = new Map<string, UploadItemState>();
  /** Which batch each item belongs to. */
  private itemBatch = new Map<string, string>();
  /** Per-batch context needed at settle time. */
  private batchCtx = new Map<
    string,
    {
      input: EnqueueInput;
      insertedPhotos: InsertedPhoto[];
      photosFailed: number;
      videosUploaded: number;
      videosFailed: number;
    }
  >();

  /**
   * FIX-2 — video items keep a handle on their source File so a failed
   * video can be retried from the tray WITHOUT the studio re-opening
   * the file picker. (Photos already retry internally with backoff;
   * videos were one-shot.) Cleared with the batch in clearSettled().
   */
  private videoWork = new Map<string, { batchId: string; input: VideoInput }>();

  /**
   * P2-1 — the photo twin of `videoWork`. Photos retry transfer
   * failures internally with backoff, but once an item lands on
   * 'failed' (prep rejection, attempts exhausted, or row-insert
   * failure) it was terminal: the tray offered Retry for videos only.
   * Retaining the PhotoWork lets retryPhoto() resend a failed photo
   * from the tray with the File the studio already picked. Cleared
   * with the batch in clearSettled().
   */
  private photoWorkById = new Map<string, PhotoWork>();

  private prepQueue: PhotoWork[] = [];
  private uploadQueue: PhotoWork[] = [];
  private prepActive = 0;
  private uploadActive = 0;

  private listeners = new Set<() => void>();
  private snapshotCache: EngineSnapshot | null = null;

  constructor(deps: EngineDeps, opts: EngineOptions = {}) {
    this.deps = deps;
    this.prepConcurrency = Math.max(1, opts.prepConcurrency ?? 3);
    this.uploadConcurrency = Math.max(1, opts.uploadConcurrency ?? 5);
    this.maxAttempts = Math.max(1, opts.maxAttempts ?? 3);
    this.backoffBaseMs = opts.backoffBaseMs ?? 400;
  }

  // ── Subscription (useSyncExternalStore-compatible) ─────────────

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): EngineSnapshot => {
    if (!this.snapshotCache) {
      const batches = Array.from(this.batches.values()).map((b) => ({
        ...b,
        items: b.items.map((i) => ({ ...i })),
      }));
      let inFlight = 0;
      let activeTotal = 0;
      let activeDone = 0;
      let activeFailed = 0;
      for (const b of batches) {
        for (const i of b.items) {
          // FIX-2 — 'stalled' joins the terminal set: nothing is
          // moving, so it must not keep `hasInFlightWork` (and with
          // it the beforeunload guard) true forever.
          if (
            i.stage !== 'done' &&
            i.stage !== 'failed' &&
            i.stage !== 'processing' &&
            i.stage !== 'stalled'
          ) {
            inFlight++;
          }
          if (!b.settled) {
            activeTotal++;
            // Deliberately NOT counting 'stalled' as done — it isn't.
            if (i.stage === 'done' || i.stage === 'processing') activeDone++;
            if (i.stage === 'failed') activeFailed++;
          }
        }
      }
      this.snapshotCache = {
        batches,
        inFlightItems: inFlight,
        activeTotal,
        activeDone,
        activeFailed,
      };
    }
    return this.snapshotCache;
  };

  /** True while anything is genuinely mid-transfer (drives the
   *  beforeunload guard — closing the TAB would lose these). */
  hasInFlightWork(): boolean {
    return this.getSnapshot().inFlightItems > 0;
  }

  private emit() {
    this.snapshotCache = null;
    for (const l of this.listeners) l();
  }

  private event(name: string, itemId: string) {
    this.deps.onEvent?.(name, itemId, (this.deps.now ?? Date.now)());
  }

  // ── Enqueue ────────────────────────────────────────────────────

  enqueue(input: EnqueueInput): string {
    const batchId = nextId('batch');
    const items: UploadItemState[] = [];

    const photoWork: PhotoWork[] = input.photos.map((p, index) => {
      const itemId = nextId('ph');
      items.push({
        id: itemId,
        name: p.name,
        kind: 'photo',
        stage: 'queued',
        progress: 0,
        attempt: 0,
      });
      const work: PhotoWork = {
        itemId,
        batchId,
        input: p,
        sortOrder: input.startingSortOrder + index,
      };
      // P2-1 — retained so a failed photo can be retried from the tray.
      this.photoWorkById.set(itemId, work);
      return work;
    });

    const videoItems = input.videos.map((v) => {
      const itemId = nextId('vd');
      items.push({
        id: itemId,
        name: v.name,
        kind: 'video',
        stage: 'queued',
        progress: 0,
        attempt: 0,
      });
      this.videoWork.set(itemId, { batchId, input: v });
      return { itemId, input: v };
    });

    const batch: UploadBatchState = {
      id: batchId,
      galleryId: input.galleryId,
      galleryName: input.galleryName,
      projectId: input.projectId,
      items,
      settled: false,
      startedAt: (this.deps.now ?? Date.now)(),
    };
    this.batches.set(batchId, batch);
    for (const i of items) {
      this.items.set(i.id, i);
      this.itemBatch.set(i.id, batchId);
    }
    this.batchCtx.set(batchId, {
      input,
      insertedPhotos: [],
      photosFailed: 0,
      videosUploaded: 0,
      videosFailed: 0,
    });

    this.prepQueue.push(...photoWork);
    this.emit();
    this.pumpPrep();
    // Videos run alongside the photo pipeline; sequential per batch
    // (Mux direct uploads are large — parallel PUTs of multi-GB
    // files just fight each other for bandwidth).
    if (videoItems.length > 0) {
      void this.runVideos(batchId, input, videoItems);
    }
    if (items.length === 0) {
      // Degenerate but legal — settle immediately.
      this.maybeSettle(batchId);
    }
    return batchId;
  }

  /** Drop settled batches from the tray. */
  clearSettled() {
    for (const [id, b] of this.batches) {
      if (b.settled) {
        for (const i of b.items) {
          this.items.delete(i.id);
          this.itemBatch.delete(i.id);
          // FIX-2 — release the retained File handles with the batch.
          this.videoWork.delete(i.id);
          // P2-1 — same for the retained photo work.
          this.photoWorkById.delete(i.id);
        }
        this.batches.delete(id);
        this.batchCtx.delete(id);
      }
    }
    this.emit();
  }

  // ── Item state helpers ─────────────────────────────────────────

  private setStage(
    itemId: string,
    stage: UploadStage,
    patch: Partial<UploadItemState> = {},
  ) {
    const item = this.items.get(itemId);
    if (!item) return;
    item.stage = stage;
    if (patch.progress !== undefined) item.progress = patch.progress;
    if (patch.attempt !== undefined) item.attempt = patch.attempt;
    // FIX-2 — `'message' in patch` (not `!== undefined`) so a retry can
    // CLEAR a stale failure message instead of carrying it forward.
    if ('message' in patch) item.message = patch.message;
    this.emit();
  }

  /**
   * Flip an externally-tracked item (video transcode poll) to a real
   * verdict. `ok` MUST reflect an observed Mux 'ready' — FIX-2 exists
   * because the old poll called this with `true` on timeout.
   *
   * Accepts 'stalled' as a source stage too, so a manual re-check can
   * still resolve a video the poll gave up on.
   */
  resolveProcessingItem(itemId: string, ok: boolean, message?: string) {
    const item = this.items.get(itemId);
    if (!item || (item.stage !== 'processing' && item.stage !== 'stalled')) return;
    this.setStage(itemId, ok ? 'done' : 'failed', { message });
    // A stalled item wasn't terminal-with-a-verdict; settling again is
    // a no-op if the batch already settled.
    const batchId = this.itemBatch.get(itemId);
    if (batchId) this.maybeSettle(batchId);
  }

  /**
   * FIX-2 — the honest middle: uploaded, undecided. Used when the
   * bounded poll runs out of patience rather than seeing a verdict.
   */
  stallProcessingItem(itemId: string, message: string) {
    const item = this.items.get(itemId);
    if (!item || item.stage !== 'processing') return;
    this.setStage(itemId, 'stalled', { message });
    const batchId = this.itemBatch.get(itemId);
    if (batchId) this.maybeSettle(batchId);
  }

  /**
   * FIX-2 — refresh the reassurance copy on a still-processing item
   * ("Still transcoding (4 min)…") without changing its stage.
   */
  updateProcessingMessage(itemId: string, message: string) {
    const item = this.items.get(itemId);
    if (!item || item.stage !== 'processing') return;
    this.setStage(itemId, 'processing', { message });
  }

  private maybeSettle(batchId: string) {
    const batch = this.batches.get(batchId);
    const ctx = this.batchCtx.get(batchId);
    if (!batch || !ctx || batch.settled) return;
    const terminal = batch.items.every(
      (i) =>
        i.stage === 'done' ||
        i.stage === 'failed' ||
        i.stage === 'processing' ||
        i.stage === 'stalled',
    );
    if (!terminal) return;
    batch.settled = true;
    this.emit();
    this.deps.onBatchSettled({
      batch: { ...batch, items: batch.items.map((i) => ({ ...i })) },
      galleryId: batch.galleryId,
      projectId: batch.projectId,
      insertedPhotos: ctx.insertedPhotos,
      photosFailed: ctx.photosFailed,
      videosUploaded: ctx.videosUploaded,
      videosFailed: ctx.videosFailed,
    });
  }

  // ── Photo pipeline: prep pool → upload pool → row insert ───────

  private pumpPrep() {
    while (this.prepActive < this.prepConcurrency && this.prepQueue.length > 0) {
      const work = this.prepQueue.shift()!;
      this.prepActive++;
      void this.runPrep(work).finally(() => {
        this.prepActive--;
        this.pumpPrep();
      });
    }
  }

  private async runPrep(work: PhotoWork) {
    this.setStage(work.itemId, 'preparing');
    this.event('prep:start', work.itemId);
    try {
      work.prepared = await this.deps.prepPhoto(work.input.file);
      this.event('prep:done', work.itemId);
      this.uploadQueue.push(work);
      this.setStage(work.itemId, 'queued');
      this.pumpUpload();
    } catch (err) {
      // imagePrep itself never rejects for decode problems — a
      // rejection here is HEIC conversion or something equally
      // structural. Fail the item visibly rather than uploading
      // bytes we couldn't read.
      this.event('prep:failed', work.itemId);
      this.failPhoto(work, (err as Error)?.message ?? 'Could not read this file');
    }
  }

  private pumpUpload() {
    while (this.uploadActive < this.uploadConcurrency && this.uploadQueue.length > 0) {
      const work = this.uploadQueue.shift()!;
      this.uploadActive++;
      void this.runUpload(work).finally(() => {
        this.uploadActive--;
        this.pumpUpload();
      });
    }
  }

  private async runUpload(work: PhotoWork) {
    const sleep =
      this.deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
    const prepared = work.prepared!;
    this.event('upload:start', work.itemId);

    for (let attempt = 1; attempt <= this.maxAttempts; attempt++) {
      this.setStage(work.itemId, 'uploading', { attempt, progress: 0 });
      try {
        const { url } = await this.deps.uploadPhoto({
          file: prepared.file,
          path: work.input.path,
          onProgress: (fraction) =>
            this.setStage(work.itemId, 'uploading', { attempt, progress: fraction }),
        });
        this.event('upload:done', work.itemId);
        await this.savePhotoRow(work, url, prepared);
        return;
      } catch (err) {
        if (attempt === this.maxAttempts) {
          this.event('upload:failed', work.itemId);
          this.failPhoto(work, (err as Error)?.message ?? 'Upload failed');
          return;
        }
        // Exponential backoff with jitter — same curve as the old
        // parallelUpload so retry behavior doesn't regress.
        await sleep(this.backoffBaseMs * 2 ** (attempt - 1) + Math.random() * 200);
      }
    }
  }

  private async savePhotoRow(work: PhotoWork, url: string, prepared: PreparedPhoto) {
    const batchId = work.batchId;
    const batch = this.batches.get(batchId);
    const ctx = this.batchCtx.get(batchId);
    if (!batch || !ctx) return;
    this.setStage(work.itemId, 'saving');
    try {
      const inserted = await this.deps.insertPhotoRow({
        galleryId: batch.galleryId,
        fileUrl: url,
        name: work.input.name,
        sortOrder: work.sortOrder,
        width: prepared.width,
        height: prepared.height,
      });
      ctx.insertedPhotos.push({ id: inserted.id, url });
      this.setStage(work.itemId, 'done', { progress: 1 });
      this.event('insert:done', work.itemId);
      this.deps.onPhotoSaved?.(batch.galleryId);
    } catch (err) {
      // The bytes are in storage but the row didn't land — surface
      // it per-file instead of pretending it worked (GAL-7 lesson).
      this.event('insert:failed', work.itemId);
      this.failPhoto(
        work,
        (err as Error)?.message
          ? `Uploaded but failed to save: ${(err as Error).message}`
          : 'Uploaded but failed to save to gallery',
      );
      return;
    }
    this.maybeSettle(batchId);
  }

  private failPhoto(work: PhotoWork, message: string) {
    const ctx = this.batchCtx.get(work.batchId);
    if (ctx) ctx.photosFailed++;
    this.setStage(work.itemId, 'failed', { message });
    this.maybeSettle(work.batchId);
  }

  // ── Video pipeline ─────────────────────────────────────────────

  private async runVideos(
    batchId: string,
    input: EnqueueInput,
    videos: Array<{ itemId: string; input: VideoInput }>,
  ) {
    const ctx = this.batchCtx.get(batchId);
    if (!ctx) return;

    let deliveryId: string;
    try {
      const companion = await this.deps.ensureVideoDelivery(
        input.galleryId,
        input.galleryName,
        input.userId,
      );
      deliveryId = companion.id;
    } catch (err) {
      const message = (err as Error)?.message || 'Could not prepare video storage';
      for (const v of videos) {
        ctx.videosFailed++;
        this.setStage(v.itemId, 'failed', { message });
      }
      this.maybeSettle(batchId);
      return;
    }

    for (const v of videos) {
      // Each video is independent: one failure marks ITS OWN item
      // failed and the loop moves straight on. A stuck or rejected
      // file never holds up the ones behind it (FIX-2).
      await this.uploadOneVideo(batchId, input.galleryId, deliveryId, v.itemId, v.input, 1);
    }
    this.maybeSettle(batchId);
  }

  /** One video, one attempt. Never throws — it resolves the item. */
  private async uploadOneVideo(
    batchId: string,
    galleryId: string,
    deliveryId: string,
    itemId: string,
    input: VideoInput,
    attempt: number,
  ) {
    const ctx = this.batchCtx.get(batchId);
    if (!ctx) return;
    try {
      this.setStage(itemId, 'uploading', { attempt, progress: 0 });
      const mux = await this.deps.createVideoUpload(deliveryId, input.name, input.file.size);
      await this.deps.uploadVideo(mux.upload_url, input.file, (fraction) =>
        this.setStage(itemId, 'uploading', { attempt, progress: fraction }),
      );
      ctx.videosUploaded++;
      this.setStage(itemId, 'processing', {
        progress: 1,
        message: 'Uploaded. Mux is transcoding — usually under a minute.',
      });
      this.deps.onVideoUploaded?.(galleryId, deliveryId, mux.delivery_file_id, itemId);
    } catch (err) {
      ctx.videosFailed++;
      this.setStage(itemId, 'failed', {
        attempt,
        message:
          (err as Error)?.message ||
          'This video didn’t reach the server. Press Retry to send it again.',
      });
    }
  }

  /**
   * FIX-2 — retry a FAILED video from the tray, reusing the File the
   * studio already picked. Returns false when the item can't be
   * retried (wrong kind/stage, or the batch was already cleared).
   */
  retryVideo(itemId: string): boolean {
    const item = this.items.get(itemId);
    const work = this.videoWork.get(itemId);
    if (!item || !work || item.kind !== 'video') return false;
    if (item.stage !== 'failed') return false;
    const ctx = this.batchCtx.get(work.batchId);
    const batch = this.batches.get(work.batchId);
    if (!ctx || !batch) return false;

    // This item is moving again, so the batch is no longer settled and
    // its failure no longer counts.
    ctx.videosFailed = Math.max(0, ctx.videosFailed - 1);
    batch.settled = false;
    const attempt = (item.attempt ?? 1) + 1;
    this.setStage(itemId, 'queued', { attempt, message: undefined });

    void (async () => {
      try {
        const companion = await this.deps.ensureVideoDelivery(
          ctx.input.galleryId,
          ctx.input.galleryName,
          ctx.input.userId,
        );
        await this.uploadOneVideo(
          work.batchId,
          ctx.input.galleryId,
          companion.id,
          itemId,
          work.input,
          attempt,
        );
      } catch (err) {
        ctx.videosFailed++;
        this.setStage(itemId, 'failed', {
          attempt,
          message:
            (err as Error)?.message ||
            'Couldn’t prepare video storage for this gallery. Try again in a moment.',
        });
      } finally {
        this.maybeSettle(work.batchId);
      }
    })();
    return true;
  }

  /**
   * P2-1 — retry a FAILED photo from the tray, reusing the File the
   * studio already picked. Mirrors retryVideo: returns false when the
   * item can't be retried (wrong kind/stage, or the batch was already
   * cleared). The item re-enters the pipeline at PREP, so all three
   * photo failure modes are covered: a prep rejection re-preps, an
   * exhausted transfer re-uploads (fresh attempt budget), and an
   * "uploaded but failed to save" re-runs the row insert. A re-upload
   * after an insert failure writes a second storage object (object
   * keys are timestamped, so no collision) — a few duplicate bytes
   * beat a dead end.
   */
  retryPhoto(itemId: string): boolean {
    const item = this.items.get(itemId);
    const work = this.photoWorkById.get(itemId);
    if (!item || !work || item.kind !== 'photo') return false;
    if (item.stage !== 'failed') return false;
    const ctx = this.batchCtx.get(work.batchId);
    const batch = this.batches.get(work.batchId);
    if (!ctx || !batch) return false;

    // This item is moving again, so the batch is no longer settled and
    // its failure no longer counts (same bookkeeping as retryVideo).
    ctx.photosFailed = Math.max(0, ctx.photosFailed - 1);
    batch.settled = false;
    work.prepared = undefined;
    this.setStage(itemId, 'queued', { progress: 0, message: undefined });
    this.prepQueue.push(work);
    this.pumpPrep();
    return true;
  }
}
